// Pure pricing maths for the Totale Volumes screen. No React in here.
// Mirrors the order pricing rules (prepare_order_pricing_df) except the parts
// Chris's sheet does not have: partner products and the weather/registration
// test. Treat the result as an ESTIMATE; the final price is set on the order.

const NO_LIMIT = 999; // pricing2 uses 999 for "no maximum dosage"

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));

/** Thresholds for the crop = the first row. Warn when rows disagree. */
export function defaultThresholds(rows) {
  if (!rows || rows.length === 0) return { tier1MinRpha: 0, tier2MinRpha: 0, tier3MinInvoice: 0 };
  const first = rows[0];
  return {
    tier1MinRpha: num(first.tier1_min_rpha),
    tier2MinRpha: num(first.tier2_min_rpha),
    tier3MinInvoice: num(first.tier3_min_invoice),
  };
}

/** Returns a list of rows whose thresholds differ from the first row. */
export function thresholdDifferences(rows) {
  if (!rows || rows.length === 0) return [];
  const base = defaultThresholds(rows);
  return rows.filter(
    (r) =>
      num(r.tier1_min_rpha) !== base.tier1MinRpha ||
      num(r.tier2_min_rpha) !== base.tier2MinRpha ||
      num(r.tier3_min_invoice) !== base.tier3MinInvoice
  );
}

/** Tier 3 price is not stored: list x (1 - (tier2% + tier3%)). Same as the Tier sheet. */
export function tier3Price(row) {
  const list = num(row.list_price_pv);
  return list * (1 - (num(row.tier2_discount_pct) + num(row.tier3_discount_pct)) / 100);
}

/**
 * @param rows        pricing2 rows for the crop (agent prices already applied by the server)
 * @param lines       [{ rowId, boxes }]  rowId = pricing2.id
 * @param hectares    number
 * @param thresholds  { tier1MinRpha, tier2MinRpha, tier3MinInvoice }
 * @param options     { applySeasonCap = true, resistance = null }
 *   resistance: Map(pricing2.id -> unit price | null) for the main product of a bundle (Silvalis). A number = the bundle is
 *   complete: the line is priced at that resistance price instead of the tier prices. null = not complete: the line is
 *   priced like any other product, from its own pricing2 tier data (the data decides, nothing is forced to list price).
 */
export function calcVolumes(rows, lines, hectares, thresholds, options = {}) {
  const { applySeasonCap = true, resistance = null } = options;
  const ha = num(hectares);
  const byId = new Map(rows.map((r) => [r.id, r]));

  // Pass 1: list values (needed before the tier can be known)
  const seen = new Map();
  const base = lines.map((line) => {
    const row = byId.get(line.rowId);
    if (!row) return { ...line, row: null, empty: true };
    seen.set(row.id, (seen.get(row.id) || 0) + 1);
    const boxes = num(line.boxes);
    const volume = boxes * num(row.full_pack_volume);
    const listPrice = num(row.list_price_pv);
    return { ...line, row, boxes, volume, listPrice, listValue: volume * listPrice };
  });

  const totalList = base.reduce((s, l) => s + (l.listValue || 0), 0);
  const listRpha = ha > 0 ? totalList / ha : 0;

  const tier1Reached = ha > 0 && listRpha >= thresholds.tier1MinRpha;
  const tier2Reached = ha > 0 && listRpha >= thresholds.tier2MinRpha;
  // Tier 3 needs Tier 1 or Tier 2 as well as the invoice total (Tier 2 always includes Tier 1, so this is "Tier 1 reached")
  const tier3Reached = (tier1Reached || tier2Reached) && totalList > 0 && totalList >= thresholds.tier3MinInvoice;
  const currentTier = tier3Reached ? 3 : tier2Reached ? 2 : tier1Reached ? 1 : 0;

  const groupKey = (r) => (r.product_id === undefined || r.product_id === null ? `row-${r.id}` : r.product_id); // rows without a product id never group
  // Lines of the SAME product (different pack sizes) count together for the dosage limits and the max cap.
  const groupsMap = new Map();
  base.forEach((l, i) => {
    if (l.empty) return;
    const k = groupKey(l.row);
    if (!groupsMap.has(k)) groupsMap.set(k, { productId: k, name: l.row.product_name, indexes: [], volume: 0 });
    const g = groupsMap.get(k);
    g.indexes.push(i);
    g.volume += l.volume || 0;
  });

  // Pass 2: per-line discount
  const out = base.map((l) => {
    if (l.empty) return l;
    const { row } = l;
    const group = groupsMap.get(groupKey(row));
    const grouped = group.indexes.length > 1;
    const volPerHa = ha > 0 ? l.volume / ha : 0;
    const groupVolPerHa = ha > 0 ? group.volume / ha : 0;
    const limitVolPerHa = grouped ? groupVolPerHa : volPerHa; // what the dosage checks look at
    const maxDosage = num(row.max_dosage);
    const hasMax = maxDosage > 0 && maxDosage < NO_LIMIT;
    const minDosage = num(row.tier2_min_dosage);

    // Qualifying volume: the product's total is capped at max dosage x hectares; split pro rata over its lines
    const groupQualifying = hasMax ? Math.min(group.volume, maxDosage * ha) : group.volume;
    const qualifying = group.volume > 0 ? groupQualifying * (l.volume / group.volume) : 0;

    const t1Price = num(row.tier1_price_pv) > 0 ? num(row.tier1_price_pv) : l.listPrice;
    const t2Price = num(row.tier2_price_pv) > 0 ? num(row.tier2_price_pv) : l.listPrice;

    const tier2Allowed = tier2Reached && limitVolPerHa >= minDosage;
    const d1 = tier1Reached ? Math.max(0, (l.listPrice - t1Price) * qualifying) : 0;
    const d2 = tier2Allowed ? Math.max(0, (l.listPrice - t2Price) * qualifying) : 0;
    const d3 = tier3Reached ? (num(row.tier3_discount_pct) / 100) * qualifying * l.listPrice : 0;
    // Bundle main product (Silvalis): when the bundle is complete the resistance price replaces the tier prices on the whole volume
    // (no max-dosage cap, as in the Hybrid sheet). Otherwise the line follows its own tier data like every other product.
    const bundleMain = !!resistance && resistance.has(row.id);
    const resistanceRaw = bundleMain ? resistance.get(row.id) : null;
    const isResistance = bundleMain && resistanceRaw !== null && resistanceRaw !== undefined;
    const resistanceUnit = isResistance ? (num(resistanceRaw) > 0 ? Math.min(num(resistanceRaw), l.listPrice) : l.listPrice) : null;
    const d3Full = tier3Reached ? (num(row.tier3_discount_pct) / 100) * l.volume * l.listPrice : 0;
    const discount = isResistance ? Math.max(0, (l.listPrice - resistanceUnit) * l.volume) + d3Full : Math.max(d1, d2) + d3;

    let unitPrice = l.volume > 0 ? (l.listValue - discount) / l.volume : l.listPrice;
    const season = num(row.season_price_pv);
    const seasonCapped = applySeasonCap && l.volume > 0 && season > 0 && unitPrice > season;
    if (seasonCapped) unitPrice = season;
    const myPackValue = unitPrice * l.volume;

    const checks = [];
    if (seen.get(row.id) > 1) checks.push("Duplicate");
    if (hasMax && l.volume > 0 && limitVolPerHa > maxDosage) checks.push("Over max");
    if (minDosage > 0 && l.volume > 0 && limitVolPerHa < minDosage) checks.push("Under min");

    return {
      ...l,
      volPerHa,
      groupVolPerHa: grouped ? groupVolPerHa : null,
      grouped,
      maxDosage: hasMax ? maxDosage : null,
      minDosage: minDosage > 0 ? minDosage : null,
      qualifying,
      isResistance,
      bundleMain,
      resistancePriced: isResistance && resistanceUnit < l.listPrice,
      appliedTier: isResistance ? (d3Full > 0 ? 3 : 0) : d3 > 0 && Math.max(d1, d2) === 0 ? 3 : Math.max(d1, d2) === 0 ? 0 : d2 >= d1 ? 2 : 1,
      discount,
      unitPrice,
      seasonCapped,
      myPackValue,
      checks,
    };
  });

  // Subtotals for products that sit on more than one line
  const groups = [...groupsMap.values()]
    .filter((g) => g.indexes.length > 1)
    .map((g) => {
      const members = g.indexes.map((i) => out[i]);
      const volume = members.reduce((s, m) => s + (m.volume || 0), 0);
      const first = members[0];
      return {
        productId: g.productId,
        name: g.name,
        indexes: g.indexes,
        boxes: members.reduce((s, m) => s + (m.boxes || 0), 0),
        volume,
        volPerHa: ha > 0 ? volume / ha : 0,
        minDosage: first.minDosage,
        maxDosage: first.maxDosage,
        checks: [...new Set(members.flatMap((m) => m.checks.filter((c) => c !== "Duplicate")))],
        listValue: members.reduce((s, m) => s + (m.listValue || 0), 0),
        myPackValue: members.reduce((s, m) => s + (m.myPackValue || 0), 0),
      };
    });

  const totalMyPack = out.reduce((s, l) => s + (l.myPackValue || 0), 0);

  // Hint: what is needed for the next tier
  let nextTier = null;
  if (currentTier < 3 && ha > 0) {
    const targets = [
      { tier: 1, needTotal: thresholds.tier1MinRpha * ha },
      { tier: 2, needTotal: thresholds.tier2MinRpha * ha },
      { tier: 3, needTotal: thresholds.tier3MinInvoice },
    ].filter((t) => t.tier > currentTier);
    const t = targets[0];
    nextTier = { tier: t.tier, moreNeeded: Math.max(0, t.needTotal - totalList) };
  }

  return {
    lines: out,
    groups,
    totals: {
      totalList,
      listRpha,
      totalMyPack,
      totalDiscount: totalList - totalMyPack,
      myPackRpha: ha > 0 ? totalMyPack / ha : 0,
      totalBoxes: out.reduce((s, l) => s + (l.boxes || 0), 0),
    },
    currentTier,
    nextTier,
  };
}
